/**
 * Auth observability service for tracking authentication events.
 * 
 * POC Scope: Sign-in tracking only
 * Future: Will expand to include SSO and other auth flows
 */

import { startJourney, trackEvent } from '../utils/event-tracker';

/**
 * Authentication method types.
 */
export type AuthMethod = 'email' | 'google' | 'facebook' | 'apple';

export type AuthErrorCategory = 
  | 'invalid_credentials'
  | 'network_error'
  | 'validation_error'
  | 'unknown_error';


/**
 * Parameters for tracking a sign-up attempt.
 */
export interface SignupAttemptParams {
  /** Authentication method (POC: only 'email') */
  authMethod: AuthMethod;
  
  /** Tenant ID from component context (optional, recommended) */
  tenantId?: string;
}

/**
 * Parameters for tracking a successful sign-up.
 */
export interface SignupSuccessParams {
  /** User ID assigned by the backend */
  userId: string;
  
  /** Session ID from the attempt event */
  sessionId: string;
  
  /** Authentication method used */
  authMethod: AuthMethod;
  
  /** Tenant ID from component context (optional, recommended) */
  tenantId?: string;
}

/**
 * Parameters for tracking a failed sign-up.
 */
export interface SignupFailureParams {
  /** The error that caused the failure */
  error: any;
  
  /** Session ID from the attempt event */
  sessionId: string;
  
  /** Authentication method used */
  authMethod: AuthMethod;
  
  /** Tenant ID from component context (optional, recommended) */
  tenantId?: string;
}

/**
 * Parameters for tracking a sign-in attempt.
 */
export interface SigninAttemptParams {
  /** Authentication method */
  authMethod: AuthMethod;
  
  /** Tenant ID from component context */
  tenantId: string;
}

/**
 * Parameters for tracking a successful sign-in.
 */
export interface SigninSuccessParams {
  /** User ID from authentication response */
  userId: string;
  
  /** Session ID from the attempt event */
  sessionId: string;
  
  /** Authentication method used */
  authMethod: AuthMethod;
  
  /** Tenant ID from component context */
  tenantId: string;
}

/**
 * Parameters for tracking a failed sign-in.
 */
export interface SigninFailureParams {
  /** The error that caused the failure */
  error: unknown;
  
  /** Category of the error */
  errorCategory: AuthErrorCategory;
  
  /** Session ID from the attempt event */
  sessionId: string;
  
  /** Authentication method used */
  authMethod: AuthMethod;
  
  /** Tenant ID from component context */
  tenantId: string;
}

function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown error';
  }
  
  if (typeof error === 'object' && error !== null) {
    const err = error as { errors?: Array<{ message?: string; title?: string }>; message?: string };
    
    if (err.errors && Array.isArray(err.errors) && err.errors.length > 0) {
      return err.errors.map(e => e.message || e.title || 'Unknown error').join(', ');
    }
    
    if (err.message) {
      return err.message;
    }
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'Unknown error';
}

export const AuthObservabilityService = {
  trackSigninAttempt(params: SigninAttemptParams): string {
    const { authMethod, tenantId } = params;
    
    return startJourney('auth_signin_attempt', {
      authMethod,
    }, tenantId);
  },

  trackSigninSuccess(params: SigninSuccessParams): void {
    const { userId, sessionId, authMethod, tenantId } = params;
    
    trackEvent('auth_signin_success', {
      userId,
      sessionId,
      authMethod,
    }, tenantId);
  },

  trackSigninFailure(params: SigninFailureParams): void {
    const { error, errorCategory, sessionId, authMethod, tenantId } = params;
    
    trackEvent('auth_signin_failure', {
      errorMessage: extractErrorMessage(error),
      errorCategory,
      sessionId,
      authMethod,
    }, tenantId);
  },
} as const;

